import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import TicketAgentBar from './TicketAgentBar';
import { JiraErrorBanner } from './JiraRateLimitBanner';
import TicketKeyActions from './TicketKeyActions';
import { useProject } from '../context/ProjectContext';
import { notifyJiraError } from '../utils/jiraErrors';
import { buildStatusPickerItems } from '../utils/jiraTransitions';


interface TicketDetailPanelProps {
  ticketKey: string;
  ticketData: {
    key: string;
    summary: string;
    status: string;
    assignee?: string | null;
    reporter?: string | null;
    priority?: string | null;
    labels?: string[];
    sprint?: string | null;
  };
  onClose: () => void;
  showTransitions?: boolean;
  onTransition?: (transitionId: string) => void;
  /** Called after assignee changes so parent lists can refresh */
  onAssigneeChange?: (ticketKey: string, assignee: string | null) => void;
  /** Called after sprint changes so parent lists can refresh */
  onSprintChange?: (ticketKey: string, sprint: string | null) => void;
  /** Called after summary changes so parent lists can refresh */
  onSummaryChange?: (ticketKey: string, summary: string) => void;
  /** Known assignees for the picker (from ticket catalog) */
  assigneeOptions?: string[];
  /** Secondary assignees (other teams) — collapsed under "Ostatní" */
  assigneeOtherOptions?: string[];
  expandedSection?: 'plan' | 'implement' | 'push' | 'log_work' | 'review' | null;
  /** Sections open on mount (e.g. description, comments). */
  initialExpandedSections?: string[];
  /** All project statuses for the status picker (optional). */
  projectStatuses?: string[];
}

interface TicketWorklog {
  id: string;
  timeSpent: string;
  timeSpentSeconds: number;
  comment?: any;
  started: string;
  updated: string;
  author?: {
    displayName?: string;
    emailAddress?: string;
    accountId?: string;
  };
  isMine?: boolean;
}

const WORKLOG_SHORTCUTS = [
  { label: 'CR 15m', time: '0.25', comment: 'CR' },
  { label: 'Solving 30m', time: '0.5', comment: 'Solving' },
  { label: 'Implementation 2h', time: '2', comment: 'Implementation' },
];

const TicketDetailPanel: React.FC<TicketDetailPanelProps> = ({
  ticketKey,
  ticketData,
  onClose,
  showTransitions = false,
  onTransition,
  onAssigneeChange,
  onSprintChange,
  onSummaryChange,
  assigneeOptions = [],
  assigneeOtherOptions = [],
  expandedSection = null,
  initialExpandedSections = [],
  projectStatuses: projectStatusesProp = [],
}) => {
  const { currentProject, jiraUrl } = useProject();
  // Initialize with ticketData immediately (no loading delay!)
  const [ticketDetail, setTicketDetail] = useState<any>(ticketData || null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [projectStatuses, setProjectStatuses] = useState<string[]>(projectStatusesProp);
  const [showTransitionsDropdown, setShowTransitionsDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showOtherAssignees, setShowOtherAssignees] = useState(false);
  const [updatingAssignee, setUpdatingAssignee] = useState(false);
  const [currentAssignee, setCurrentAssignee] = useState<string | null>(ticketData.assignee ?? null);
  const [showSprintDropdown, setShowSprintDropdown] = useState(false);
  const [updatingSprint, setUpdatingSprint] = useState(false);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [sprintOptions, setSprintOptions] = useState<
    Array<{ id: number; name: string; state: string }>
  >([]);
  const [currentSprint, setCurrentSprint] = useState<string | null>(ticketData.sprint ?? null);
  const [currentSummary, setCurrentSummary] = useState(ticketData.summary || '');
  const [editingSummary, setEditingSummary] = useState(false);
  const [editSummaryText, setEditSummaryText] = useState(ticketData.summary || '');
  const [savingSummary, setSavingSummary] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionText, setEditDescriptionText] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set(initialExpandedSections);
    if (expandedSection) initial.add(expandedSection);
    return initial;
  });
  // Comment state
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  // Worklog state
  const [worklogs, setWorklogs] = useState<TicketWorklog[]>([]);
  const [loadingWorklogs, setLoadingWorklogs] = useState(false);
  const [loggingWork, setLoggingWork] = useState(false);
  const [timeSpent, setTimeSpent] = useState('');
  const [worklogComment, setWorklogComment] = useState('');
  const [worklogDate, setWorklogDate] = useState(() => {
    const n = new Date();
    const p = (x: number) => String(x).padStart(2, '0');
    return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
  });
  const [editingWorklogId, setEditingWorklogId] = useState<string | null>(null);
  const [editTimeSpent, setEditTimeSpent] = useState('');
  const [editWorklogComment, setEditWorklogComment] = useState('');
  const [jiraError, setJiraError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch full details in background (don't block UI)
    setJiraError(null);
    setCurrentAssignee(ticketData.assignee ?? null);
    setCurrentSprint(ticketData.sprint ?? null);
    setCurrentSummary(ticketData.summary || '');
    setEditSummaryText(ticketData.summary || '');
    setEditingSummary(false);
    setEditingDescription(false);
    fetchTicketDetail();
    fetchWorklogs();
  }, [ticketKey, currentProject?.id]);

  useEffect(() => {
    setCurrentAssignee(ticketData.assignee ?? null);
  }, [ticketData.assignee]);

  useEffect(() => {
    setCurrentSprint(ticketData.sprint ?? null);
  }, [ticketData.sprint]);

  useEffect(() => {
    setCurrentSummary(ticketData.summary || '');
    if (!editingSummary) {
      setEditSummaryText(ticketData.summary || '');
    }
  }, [ticketData.summary]);

  useEffect(() => {
    if (!currentProject?.id) return;
    let cancelled = false;
    setLoadingSprints(true);
    axios
      .get(`${API_URL}/api/tickets/sprints`, {
        params: { project_id: currentProject.id },
      })
      .then((res) => {
        if (cancelled) return;
        setSprintOptions(res.data?.sprints || []);
      })
      .catch(() => {
        if (!cancelled) setSprintOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSprints(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?.id]);

  useEffect(() => {
    if (expandedSection) {
      setExpandedSections((prev) => new Set([...prev, expandedSection]));
    }
  }, [expandedSection]);

  useEffect(() => {
    if (projectStatusesProp.length > 0) {
      setProjectStatuses(projectStatusesProp);
      return;
    }
    if (!currentProject?.id) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/tickets/filters/options`, {
        params: { project_id: currentProject.id },
      })
      .then((res) => {
        if (!cancelled && Array.isArray(res.data?.statuses)) {
          setProjectStatuses(res.data.statuses);
        }
      })
      .catch(() => {
        /* ignore — picker still works with transitions alone */
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?.id, projectStatusesProp]);

  const handleAssigneeChange = async (assignee: string | null) => {
    if (!currentProject?.id) return;
    setUpdatingAssignee(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/update`, null, {
        params: {
          project_id: currentProject.id,
          assignee: assignee ?? 'none',
        },
      });
      setCurrentAssignee(assignee);
      setShowAssigneeDropdown(false);
      onAssigneeChange?.(ticketKey, assignee);
    } catch (error) {
      setJiraError(notifyJiraError(error));
    } finally {
      setUpdatingAssignee(false);
    }
  };

  const handleSprintChange = async (sprintId: number | null, sprintName: string | null) => {
    if (!currentProject?.id) return;
    setUpdatingSprint(true);
    try {
      const res = await axios.put(`${API_URL}/api/tickets/${ticketKey}/update`, null, {
        params: {
          project_id: currentProject.id,
          ...(sprintId == null ? { to_backlog: true } : { sprint_id: sprintId }),
        },
      });
      const nextSprint =
        sprintId == null ? null : (res.data?.sprint as string | undefined) || sprintName;
      setCurrentSprint(nextSprint);
      setShowSprintDropdown(false);
      onSprintChange?.(ticketKey, nextSprint);
    } catch (error) {
      setJiraError(notifyJiraError(error));
    } finally {
      setUpdatingSprint(false);
    }
  };

  const handleSaveSummary = async () => {
    const next = editSummaryText.trim();
    if (!currentProject?.id || !next || next === currentSummary) {
      setEditingSummary(false);
      setEditSummaryText(currentSummary);
      return;
    }
    setSavingSummary(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/update`, null, {
        params: { project_id: currentProject.id, summary: next },
      });
      setCurrentSummary(next);
      setEditingSummary(false);
      onSummaryChange?.(ticketKey, next);
      setTicketDetail((prev: any) => {
        if (!prev?.ticket?.fields) return prev;
        return {
          ...prev,
          summary: next,
          ticket: {
            ...prev.ticket,
            fields: { ...prev.ticket.fields, summary: next },
          },
        };
      });
    } catch (error) {
      setJiraError(notifyJiraError(error));
    } finally {
      setSavingSummary(false);
    }
  };

  const startEditDescription = () => {
    const desc =
      ticketDetail?.ticket?.fields?.description ??
      ticketDetail?.description ??
      null;
    setEditDescriptionText(extractTextFromADF(desc));
    setEditingDescription(true);
    setExpandedSections((prev) => new Set([...prev, 'description']));
  };

  const handleSaveDescription = async () => {
    if (!currentProject?.id) return;
    setSavingDescription(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/update`, null, {
        params: {
          project_id: currentProject.id,
          description: editDescriptionText,
        },
      });
      setEditingDescription(false);
      await fetchTicketDetail();
    } catch (error) {
      setJiraError(notifyJiraError(error));
    } finally {
      setSavingDescription(false);
    }
  };

  const assigneePickerList = Array.from(
    new Set([
      ...assigneeOptions,
      ...(currentAssignee ? [currentAssignee] : []),
    ])
  ).sort((a, b) => a.localeCompare(b, 'cs'));

  const assigneeOtherList = assigneeOtherOptions
    .filter((n) => !assigneePickerList.includes(n))
    .sort((a, b) => a.localeCompare(b, 'cs'));

  const fetchWorklogs = async (silent: boolean = false) => {
    if (!silent) {
      setLoadingWorklogs(true);
    }
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${ticketKey}/worklogs`, {
        params: { project_id: currentProject?.id },
      });
      setWorklogs(response.data.worklogs || []);
    } catch (error) {
      console.error('Failed to fetch worklogs:', error);
      setJiraError(notifyJiraError(error));
      if (!silent) {
        setWorklogs([]);
      }
    } finally {
      if (!silent) {
        setLoadingWorklogs(false);
      }
    }
  };

  const extractWorklogComment = (comment: any): string => {
    if (!comment) return '';
    if (typeof comment === 'string') return comment;
    if (comment.content && Array.isArray(comment.content)) {
      return comment.content
        .map((block: any) => {
          if (block.content && Array.isArray(block.content)) {
            return block.content.map((c: any) => c.text || '').join('');
          }
          return '';
        })
        .join('\n')
        .trim();
    }
    return '';
  };

  const formatSecondsToHours = (seconds: number): number =>
    Math.round((seconds / 3600) * 100) / 100;

  const handleAddWorklog = async (time?: string, comment?: string) => {
    const spent = (time ?? timeSpent).trim();
    const note = (comment ?? worklogComment).trim();
    if (!spent) {
      alert('Enter time spent (e.g. 0.25, 0.5, 2)');
      return;
    }
    setLoggingWork(true);
    try {
      const started = worklogDate
        ? `${worklogDate}T09:00:00.000+0000`
        : undefined;
      await axios.post(`${API_URL}/api/tickets/${ticketKey}/worklog`, null, {
        params: {
          time_spent: spent,
          comment: note || undefined,
          started,
          project_id: currentProject?.id,
        },
      });
      setTimeSpent('');
      setWorklogComment('');
      await fetchWorklogs(true);
    } catch (error: any) {
      setJiraError(notifyJiraError(error));
    } finally {
      setLoggingWork(false);
    }
  };

  const handleUpdateWorklog = async (worklogId: string) => {
    if (!editTimeSpent.trim()) return;
    setLoggingWork(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/worklog/${worklogId}`, null, {
        params: {
          time_spent: editTimeSpent.trim(),
          comment: editWorklogComment.trim() || undefined,
          project_id: currentProject?.id,
        },
      });
      setEditingWorklogId(null);
      await fetchWorklogs(true);
    } catch (error: any) {
      setJiraError(notifyJiraError(error));
    } finally {
      setLoggingWork(false);
    }
  };

  const handleDeleteWorklog = async (worklogId: string) => {
    if (!window.confirm('Delete this worklog?')) return;
    setLoggingWork(true);
    try {
      await axios.delete(`${API_URL}/api/tickets/${ticketKey}/worklog/${worklogId}`, {
        params: { project_id: currentProject?.id },
      });
      if (editingWorklogId === worklogId) setEditingWorklogId(null);
      await fetchWorklogs(true);
    } catch (error: any) {
      setJiraError(notifyJiraError(error));
    } finally {
      setLoggingWork(false);
    }
  };

  const startEditWorklog = (wl: TicketWorklog) => {
    setEditingWorklogId(wl.id);
    setEditTimeSpent(String(formatSecondsToHours(wl.timeSpentSeconds)));
    setEditWorklogComment(extractWorklogComment(wl.comment));
  };

  const fetchTicketDetail = async () => {
    try {
      setLoadingDetail(true);
      const response = await axios.get(`${API_URL}/api/tickets/${ticketKey}`);
      setTicketDetail(response.data);
    } catch (err) {
      console.error('Error fetching ticket detail:', err);
      setJiraError(notifyJiraError(err));
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      await axios.post(`${API_URL}/api/tickets/${ticketKey}/comment`, null, {
        params: {
          body: newComment.trim(),
          project_id: currentProject?.id,
        },
      });
      setNewComment('');
      // Refresh ticket detail to show new comment
      await fetchTicketDetail();
    } catch (err) {
      console.error('Error adding comment:', err);
      setJiraError(notifyJiraError(err));
    } finally {
      setAddingComment(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editCommentText.trim()) return;

    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/comment/${commentId}`, null, {
        params: {
          body: editCommentText.trim(),
          project_id: currentProject?.id,
        },
      });
      setEditingCommentId(null);
      setEditCommentText('');
      // Refresh ticket detail to show updated comment
      await fetchTicketDetail();
    } catch (err) {
      console.error('Error updating comment:', err);
      setJiraError(notifyJiraError(err));
    }
  };

  const handleTransitionClick = async (transitionId: string) => {
    try {
      if (onTransition) {
        await onTransition(transitionId);
      } else {
        await axios.post(`${API_URL}/api/tickets/${ticketKey}/transition`, null, {
          params: { transition_id: transitionId, project_id: currentProject?.id },
        });
      }
      setShowTransitionsDropdown(false);
      // Always re-fetch — list cache used to leave the old status on the badge
      await fetchTicketDetail();
    } catch (err) {
      console.error('Error transitioning ticket:', err);
      setJiraError(notifyJiraError(err));
    }
  };

  const startEditComment = (commentId: string, currentBody: any) => {
    setEditingCommentId(commentId);
    // Extract text from ADF or rendered body
    let textContent = '';
    if (typeof currentBody === 'string') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = currentBody;
      textContent = tempDiv.textContent || tempDiv.innerText || '';
    } else if (currentBody && typeof currentBody === 'object') {
      // Try to extract from ADF format
      if (currentBody.content) {
        textContent = extractTextFromADF(currentBody);
      }
    }
    setEditCommentText(textContent);
  };

  // Helper to extract text from ADF (Atlassian Document Format)
  const extractTextFromADF = (adf: any): string => {
    if (!adf) return '';
    if (typeof adf === 'string') {
      const temp = document.createElement('div');
      temp.innerHTML = adf;
      return (temp.textContent || temp.innerText || adf).trim();
    }

    const blocks: string[] = [];
    const inlineText = (nodes: any[] | undefined): string => {
      if (!nodes) return '';
      return nodes
        .map((n) => {
          if (!n) return '';
          if (n.type === 'text') return n.text || '';
          if (n.content) return inlineText(n.content);
          return '';
        })
        .join('');
    };
    const walk = (node: any) => {
      if (!node) return;
      if (node.type === 'paragraph' || node.type === 'heading') {
        blocks.push(inlineText(node.content));
        return;
      }
      if (node.type === 'bulletList' || node.type === 'orderedList') {
        (node.content || []).forEach((item: any) => {
          if (item?.type === 'listItem') {
            const parts = (item.content || [])
              .map((c: any) => inlineText(c?.content || []))
              .filter(Boolean);
            blocks.push(`• ${parts.join(' ')}`);
          }
        });
        return;
      }
      if (node.type === 'codeBlock') {
        blocks.push(inlineText(node.content));
        return;
      }
      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(walk);
      }
    };
    walk(adf);
    return blocks.join('\n').trim();
  };

  // Render comment body - convert ADF to HTML with full formatting support
  const renderCommentBody = (body: any): string => {
    if (!body) return '<p>No content</p>';

    // If it's already a string (rendered HTML), use it
    if (typeof body === 'string') {
      return body;
    }

    // If it's an ADF object
    if (typeof body === 'object' && body.type === 'doc' && body.content) {
      return convertADFToHTML(body.content);
    }

    return '<p>' + String(body) + '</p>';
  };

  // Full ADF to HTML converter
  const convertADFToHTML = (content: any[]): string => {
    let html = '';

    content.forEach((node: any) => {
      switch (node.type) {
        case 'paragraph':
          html += '<p>' + convertInlineContent(node.content || []) + '</p>';
          break;

        case 'bulletList':
          html += '<ul class="list-disc pl-5 my-2">';
          node.content?.forEach((item: any) => {
            if (item.type === 'listItem') {
              html += '<li>' + convertADFToHTML(item.content || []) + '</li>';
            }
          });
          html += '</ul>';
          break;

        case 'orderedList':
          html += '<ol class="list-decimal pl-5 my-2">';
          node.content?.forEach((item: any) => {
            if (item.type === 'listItem') {
              html += '<li>' + convertADFToHTML(item.content || []) + '</li>';
            }
          });
          html += '</ol>';
          break;

        case 'heading':
          const level = node.attrs?.level || 1;
          html += `<h${level} class="font-bold text-lg my-2">` + convertInlineContent(node.content || []) + `</h${level}>`;
          break;

        case 'codeBlock':
          const code = node.content?.map((c: any) => c.text || '').join('') || '';
          html += '<pre class="bg-gray-100 p-2 rounded my-2 overflow-x-auto"><code>' + escapeHtml(code) + '</code></pre>';
          break;

        case 'blockquote':
          html += '<blockquote class="border-l-4 border-gray-300 pl-4 italic my-2">' + convertADFToHTML(node.content || []) + '</blockquote>';
          break;

        case 'rule':
          html += '<hr class="my-3 border-gray-300" />';
          break;

        default:
          // Fallback for unknown types
          if (node.content) {
            html += convertADFToHTML(node.content);
          }
      }
    });

    return html;
  };

  // Convert inline content (text, links, formatting)
  const convertInlineContent = (content: any[]): string => {
    let html = '';

    content.forEach((node: any) => {
      if (node.type === 'text') {
        let text = escapeHtml(node.text || '');

        // Apply marks (bold, italic, code, etc.)
        if (node.marks) {
          node.marks.forEach((mark: any) => {
            switch (mark.type) {
              case 'strong':
                text = `<strong>${text}</strong>`;
                break;
              case 'em':
                text = `<em>${text}</em>`;
                break;
              case 'code':
                text = `<code class="bg-gray-100 px-1 rounded">${text}</code>`;
                break;
              case 'strike':
                text = `<s>${text}</s>`;
                break;
              case 'underline':
                text = `<u>${text}</u>`;
                break;
              case 'link':
                const href = mark.attrs?.href || '#';
                text = `<a href="${escapeHtml(href)}" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">${text}</a>`;
                break;
            }
          });
        }

        html += text;
      } else if (node.type === 'hardBreak') {
        html += '<br />';
      } else if (node.type === 'inlineCard') {
        const url = node.attrs?.url || '#';
        html += `<a href="${escapeHtml(url)}" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`;
      }
    });

    return html;
  };

  // Escape HTML to prevent XSS
  const escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Helper functions
  const getPriorityIcon = (priority: string | null) => {
    switch (priority?.toLowerCase()) {
      case 'highest': return { icon: '🔺', color: 'text-red-600', title: 'Highest' };
      case 'high': return { icon: '🔺', color: 'text-orange-500', title: 'High' };
      case 'medium': return { icon: '=', color: 'text-yellow-600', title: 'Medium' };
      case 'low': return { icon: '🔻', color: 'text-blue-500', title: 'Low' };
      case 'lowest': return { icon: '🔻', color: 'text-blue-600', title: 'Lowest' };
      default: return { icon: '=', color: 'text-gray-400', title: 'No priority' };
    }
  };

  const getLabelColor = (label: string) => {
    const labelLower = label.toLowerCase();
    if (labelLower.includes('mzdy') || labelLower.includes('docházka') || labelLower.includes('plánování')) {
      return 'bg-teal-600 text-white';
    }
    if (labelLower.includes('pochůzky')) {
      return 'bg-orange-600 text-white';
    }
    return 'bg-gray-600 text-white';
  };

  const getStatusColor = (status: string | null) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('open') || statusLower.includes('otevřeno') || statusLower.includes('to do')) {
      return 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    }
    if (statusLower.includes('rozpracováno') || statusLower.includes('in progress')) {
      return 'bg-blue-400 text-white hover:bg-blue-500';
    }
    if (statusLower.includes('revize') || statusLower.includes('review')) {
      return 'bg-blue-700 text-white hover:bg-blue-800';
    }
    if (statusLower.includes('test')) {
      return 'bg-blue-900 text-white hover:bg-blue-950';
    }
    if (statusLower.includes('done') || statusLower.includes('dokončeno')) {
      return 'bg-green-100 text-green-800 hover:bg-green-200';
    }
    return 'bg-gray-200 text-gray-700 hover:bg-gray-300';
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Prefer live status from Jira detail — ticketData from catalog can be stale (cache)
  const displayStatus =
    ticketDetail?.status ||
    ticketDetail?.ticket?.fields?.status?.name ||
    ticketData.status;

  const reporterName: string | null =
    ticketDetail?.ticket?.fields?.reporter?.displayName ||
    ticketData.reporter ||
    null;

  const statusPickerItems = buildStatusPickerItems(
    projectStatuses,
    ticketDetail?.transitions || [],
    displayStatus
  );

  const sectionToggleClass =
    'w-full flex items-center gap-1.5 py-1 text-left hover:text-slate-900 dark:hover:text-white transition';

  return (
    <div className="w-full bg-white dark:bg-slate-900 rounded-xl shadow-panel px-4 py-3 overflow-y-auto max-h-[calc(100vh-5rem)] sticky top-0 border border-slate-200/80 dark:border-slate-700">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Ticket Detail</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none px-1"
          title="Close"
        >
          ×
        </button>
      </div>

      {jiraError && (
        <JiraErrorBanner
          message={jiraError}
          onRetry={() => {
            setJiraError(null);
            fetchTicketDetail();
            fetchWorklogs();
          }}
          onDismiss={() => setJiraError(null)}
        />
      )}

      {!ticketDetail ? (
        <LoadingSpinner message="Loading details..." size="small" />
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-baseline gap-2 min-w-0">
            <TicketKeyActions
              ticketKey={ticketData.key}
              jiraUrl={jiraUrl}
              keyClassName="font-mono text-sm text-blue-600 dark:text-blue-400 font-bold whitespace-nowrap"
            />
            {editingSummary ? (
              <div className="flex-1 min-w-0 space-y-1">
                <input
                  autoFocus
                  type="text"
                  value={editSummaryText}
                  onChange={(e) => setEditSummaryText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSaveSummary();
                    }
                    if (e.key === 'Escape') {
                      setEditingSummary(false);
                      setEditSummaryText(currentSummary);
                    }
                  }}
                  disabled={savingSummary}
                  className="w-full text-sm font-semibold px-2 py-1 rounded border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-400/40"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSaveSummary()}
                    disabled={savingSummary || !editSummaryText.trim()}
                    className="text-[10px] text-green-600 hover:underline disabled:text-gray-400"
                  >
                    {savingSummary ? 'Ukládám…' : 'Uložit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSummary(false);
                      setEditSummaryText(currentSummary);
                    }}
                    disabled={savingSummary}
                    className="text-[10px] text-red-600 hover:underline disabled:text-gray-400"
                  >
                    Zrušit
                  </button>
                </div>
              </div>
            ) : (
              <h3
                className="text-sm font-semibold text-gray-800 dark:text-white flex-1 leading-snug cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded px-1 -mx-1 transition"
                title="Klikni pro editaci názvu"
                onClick={() => {
                  setEditSummaryText(currentSummary);
                  setEditingSummary(true);
                }}
              >
                {currentSummary}
              </h3>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap text-xs pb-2 border-b border-slate-100 dark:border-slate-700/80">
            {ticketData.labels && ticketData.labels.length > 0 && (
              <>
                {ticketData.labels.map((label) => (
                  <span
                    key={label}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${getLabelColor(label)}`}
                  >
                    {label}
                  </span>
                ))}
                <span className="text-gray-300 dark:text-gray-600">·</span>
              </>
            )}

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (showTransitions) {
                    setShowAssigneeDropdown(false);
                    setShowSprintDropdown(false);
                    setShowTransitionsDropdown(!showTransitionsDropdown);
                  }
                }}
                className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase flex items-center gap-0.5 ${getStatusColor(displayStatus)}`}
              >
                {displayStatus}
                <span className="text-[9px] opacity-70">▼</span>
              </button>

              {showTransitions && showTransitionsDropdown && (
                <div className="absolute z-50 mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg min-w-[180px] max-h-72 overflow-y-auto">
                  {statusPickerItems.length === 0 ? (
                    <div className="px-2.5 py-1.5 text-xs text-gray-500">Načítám…</div>
                  ) : (
                    statusPickerItems.map((item) => (
                      <button
                        key={item.status}
                        type="button"
                        disabled={item.isCurrent || !item.transitionId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!item.transitionId) return;
                          handleTransitionClick(item.transitionId);
                          setShowTransitionsDropdown(false);
                        }}
                        className={`block w-full text-left px-2.5 py-1.5 text-xs transition ${
                          item.isCurrent
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 font-semibold'
                            : 'text-gray-800 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-600'
                        }`}
                        title={item.isCurrent ? 'Aktuální stav' : item.status}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>{item.status}</span>
                          {item.isCurrent && <span className="text-[10px]">●</span>}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {ticketData.priority && (() => {
              const priority = getPriorityIcon(ticketData.priority);
              return (
                <span className={`text-sm font-bold ${priority.color}`} title={priority.title}>
                  {priority.icon}
                </span>
              );
            })()}

            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTransitionsDropdown(false);
                  setShowSprintDropdown(false);
                  setShowOtherAssignees(false);
                  setShowAssigneeDropdown(!showAssigneeDropdown);
                }}
                disabled={updatingAssignee || !currentProject?.id}
                className="flex items-center gap-1 hover:opacity-80 transition disabled:opacity-50"
                title={currentAssignee ? `Změnit assignee (${currentAssignee})` : 'Přiřadit osobu'}
              >
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Zodp.</span>
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold">
                  {getInitials(currentAssignee)}
                </div>
                <span className="text-gray-600 dark:text-gray-300 text-xs">
                  {currentAssignee || 'Unassigned'}
                </span>
                <span className="text-[9px] text-gray-400">▼</span>
              </button>
              {showAssigneeDropdown && (
                <div className="absolute z-50 mt-1 left-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg min-w-[160px] max-h-48 overflow-y-auto">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAssigneeChange(null);
                    }}
                    className={`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-gray-600 ${
                      !currentAssignee ? 'font-semibold text-blue-600' : 'text-gray-800 dark:text-white'
                    }`}
                  >
                    Unassigned
                  </button>
                  {assigneePickerList.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssigneeChange(name);
                      }}
                      className={`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-gray-600 ${
                        currentAssignee === name
                          ? 'font-semibold text-blue-600'
                          : 'text-gray-800 dark:text-white'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                  {assigneeOtherList.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowOtherAssignees((v) => !v);
                        }}
                        className="w-full text-left px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 border-t border-gray-200 dark:border-gray-600"
                      >
                        {showOtherAssignees
                          ? 'Skrýt ostatní'
                          : `Ostatní (${assigneeOtherList.length})`}
                      </button>
                      {showOtherAssignees &&
                        assigneeOtherList.map((name) => (
                          <button
                            key={name}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssigneeChange(name);
                            }}
                            className={`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-gray-600 ${
                              currentAssignee === name
                                ? 'font-semibold text-blue-600'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {name}
                          </button>
                        ))}
                    </>
                  )}
                  {assigneePickerList.length === 0 && assigneeOtherList.length === 0 && (
                    <div className="px-2.5 py-1.5 text-[10px] text-gray-400">Žádné osoby v katalogu</div>
                  )}
                </div>
              )}
            </div>

            {reporterName && (
              <div
                className="flex items-center gap-1"
                title={`Zadavatel: ${reporterName}`}
              >
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Zad.</span>
                <div className="w-5 h-5 rounded-full bg-slate-400 text-white flex items-center justify-center text-[10px] font-bold">
                  {getInitials(reporterName)}
                </div>
                <span className="text-gray-600 dark:text-gray-300 text-xs">
                  {reporterName}
                </span>
              </div>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTransitionsDropdown(false);
                  setShowAssigneeDropdown(false);
                  setShowOtherAssignees(false);
                  setShowSprintDropdown(!showSprintDropdown);
                }}
                disabled={updatingSprint || !currentProject?.id}
                className="flex items-center gap-1 hover:opacity-80 transition disabled:opacity-50"
                title="Změnit sprint"
              >
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Sprint</span>
                <span className="text-gray-600 dark:text-gray-300 text-xs">
                  {currentSprint || 'Backlog'}
                </span>
                <span className="text-[9px] text-gray-400">▼</span>
              </button>
              {showSprintDropdown && (
                <div className="absolute z-50 mt-1 left-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded shadow-lg min-w-[180px] max-h-56 overflow-y-auto">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSprintChange(null, null);
                    }}
                    className={`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-gray-600 ${
                      !currentSprint
                        ? 'font-semibold text-blue-600'
                        : 'text-gray-800 dark:text-white'
                    }`}
                  >
                    Backlog
                  </button>
                  {loadingSprints && (
                    <div className="px-2.5 py-1.5 text-[10px] text-gray-400">Načítám sprinty…</div>
                  )}
                  {!loadingSprints && sprintOptions.length === 0 && (
                    <div className="px-2.5 py-1.5 text-[10px] text-amber-600 dark:text-amber-400">
                      Žádné aktivní/future sprinty
                    </div>
                  )}
                  {sprintOptions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSprintChange(s.id, s.name);
                      }}
                      className={`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-gray-600 ${
                        currentSprint === s.name
                          ? 'font-semibold text-blue-600'
                          : 'text-gray-800 dark:text-white'
                      }`}
                    >
                      {s.name}
                      {s.state === 'active' ? (
                        <span className="ml-1 text-[10px] text-green-600 dark:text-green-400">
                          aktivní
                        </span>
                      ) : s.state === 'future' ? (
                        <span className="ml-1 text-[10px] text-blue-500 dark:text-blue-300">
                          budoucí
                        </span>
                      ) : s.state === 'closed' ? (
                        <span className="ml-1 text-[10px] text-gray-400">
                          uzavřený
                        </span>
                      ) : null}
                    </button>
                  ))}
                  {!loadingSprints &&
                    currentSprint &&
                    !sprintOptions.some((s) => s.name === currentSprint) && (
                      <div className="px-2.5 py-1.5 text-[10px] text-gray-400 border-t border-gray-200 dark:border-gray-600">
                        Aktuálně: {currentSprint} (uzavřený)
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {ticketDetail && (
            <div>
              <button
                type="button"
                onClick={() => toggleSection('description')}
                className={sectionToggleClass}
              >
                <span className="text-[10px] text-gray-400 w-3 shrink-0">
                  {expandedSections.has('description') ? '▼' : '▶'}
                </span>
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Description</span>
              </button>
              {expandedSections.has('description') && (
                <div className="pl-4">
                  {editingDescription ? (
                    <div className="space-y-1.5">
                      <textarea
                        autoFocus
                        value={editDescriptionText}
                        onChange={(e) => setEditDescriptionText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setEditingDescription(false);
                          }
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                            e.preventDefault();
                            void handleSaveDescription();
                          }
                        }}
                        rows={6}
                        disabled={savingDescription}
                        className="w-full text-xs px-2 py-1.5 rounded border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-400/40 resize-y"
                        placeholder="Popis tiketu…"
                      />
                      <div className="flex gap-2 items-center">
                        <button
                          type="button"
                          onClick={() => void handleSaveDescription()}
                          disabled={savingDescription}
                          className="text-[10px] text-green-600 hover:underline disabled:text-gray-400"
                        >
                          {savingDescription ? 'Ukládám…' : 'Uložit'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingDescription(false)}
                          disabled={savingDescription}
                          className="text-[10px] text-red-600 hover:underline disabled:text-gray-400"
                        >
                          Zrušit
                        </button>
                        <span className="text-[10px] text-gray-400">Ctrl+Enter uloží</span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-xs text-gray-700 dark:text-gray-300 prose prose-sm max-w-none leading-relaxed cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded px-1 -mx-1 transition min-h-[1.5rem]"
                      title="Klikni pro editaci popisu"
                      onClick={startEditDescription}
                      dangerouslySetInnerHTML={{
                        __html: renderCommentBody(ticketDetail.ticket?.fields?.description)
                          || ticketDetail.ticket?.renderedFields?.description
                          || '<p class="text-gray-400 italic">Žádný popis — klikni pro přidání</p>',
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          {ticketDetail && ticketDetail.comments && (
            <div>
              <button
                type="button"
                onClick={() => toggleSection('comments')}
                className={sectionToggleClass}
              >
                <span className="text-[10px] text-gray-400 w-3 shrink-0">
                  {expandedSections.has('comments') ? '▼' : '▶'}
                </span>
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Comments ({ticketDetail.comments.length})
                </span>
              </button>

              {expandedSections.has('comments') && (
                <div className="pl-4 space-y-2">
                  <div>
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment…"
                      className="w-full text-xs border-0 border-b border-slate-200 dark:border-slate-600 rounded-none p-1.5 bg-transparent text-gray-800 dark:text-white focus:outline-none focus:border-blue-400"
                      rows={2}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={addingComment || !newComment.trim()}
                      className="mt-1 text-xs text-blue-600 hover:underline disabled:text-gray-400"
                    >
                      {addingComment ? 'Adding…' : 'Add'}
                    </button>
                  </div>

                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {ticketDetail.comments.map((comment: any) => (
                      <div key={comment.id} className="py-1.5 border-b border-slate-100 dark:border-slate-700/60 last:border-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-semibold text-xs text-gray-800 dark:text-white truncate">
                              {comment.author?.displayName || 'Unknown'}
                            </span>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {new Date(comment.created).toLocaleString('cs-CZ', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {editingCommentId === comment.id ? (
                            <div className="flex gap-2 shrink-0">
                              <button
                                onClick={() => handleEditComment(comment.id)}
                                className="text-[10px] text-green-600 hover:underline"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCommentId(null);
                                  setEditCommentText('');
                                }}
                                className="text-[10px] text-red-600 hover:underline"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditComment(comment.id, comment.body)}
                              className="text-[10px] text-blue-600 hover:underline shrink-0"
                            >
                              Edit
                            </button>
                          )}
                        </div>

                        {editingCommentId === comment.id ? (
                          <textarea
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            className="w-full text-xs border border-slate-200 dark:border-slate-600 rounded p-1.5 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none"
                            rows={2}
                          />
                        ) : (
                          <div
                            className="text-xs text-gray-700 dark:text-gray-300 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: renderCommentBody(comment.body) }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Developer — AI běží jen v Complic chatu */}
          <div className="pt-1.5 border-t border-slate-100 dark:border-slate-700/80">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
              Developer
            </div>
            <TicketAgentBar ticketKey={ticketKey} />
          </div>

          {/* Log Work */}
          <div className="pt-1.5 border-t border-slate-100 dark:border-slate-700/80">
            <button
              type="button"
              onClick={() => toggleSection('log_work')}
              className={sectionToggleClass}
            >
              <span className="text-[10px] text-gray-400 w-3 shrink-0">
                {expandedSections.has('log_work') ? '▼' : '▶'}
              </span>
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                Log Work{worklogs.length > 0 ? ` (${worklogs.length})` : ''}
              </span>
            </button>
            {expandedSections.has('log_work') && (
              <div className="pl-4 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {WORKLOG_SHORTCUTS.map((shortcut) => (
                    <button
                      key={shortcut.label}
                      type="button"
                      disabled={loggingWork}
                      onClick={() => handleAddWorklog(shortcut.time, shortcut.comment)}
                      className="px-1.5 py-0.5 text-[10px] font-medium rounded text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                    >
                      {shortcut.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-1.5 items-center flex-wrap">
                  <input
                    type="date"
                    value={worklogDate}
                    onChange={(e) => setWorklogDate(e.target.value)}
                    className="px-1.5 py-1 text-xs border-0 border-b border-slate-200 dark:border-slate-600 bg-transparent dark:text-white focus:outline-none focus:border-blue-400"
                    disabled={loggingWork}
                    title="Datum worklogu"
                  />
                  <input
                    type="text"
                    value={worklogComment}
                    onChange={(e) => setWorklogComment(e.target.value)}
                    placeholder="Comment"
                    className="flex-1 min-w-[6rem] px-1.5 py-1 text-xs border-0 border-b border-slate-200 dark:border-slate-600 bg-transparent dark:text-white focus:outline-none focus:border-blue-400"
                    disabled={loggingWork}
                  />
                  <input
                    type="text"
                    value={timeSpent}
                    onChange={(e) => setTimeSpent(e.target.value)}
                    placeholder="h"
                    className="w-14 px-1.5 py-1 text-xs border-0 border-b border-slate-200 dark:border-slate-600 bg-transparent dark:text-white focus:outline-none focus:border-blue-400"
                    disabled={loggingWork}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddWorklog()}
                  />
                  <button
                    type="button"
                    disabled={loggingWork || !timeSpent.trim()}
                    onClick={() => handleAddWorklog()}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    {loggingWork ? '…' : 'Add'}
                  </button>
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {loadingWorklogs && worklogs.length === 0 ? (
                    <div className="text-[10px] text-gray-400">Loading…</div>
                  ) : worklogs.length === 0 ? (
                    <div className="text-[10px] text-gray-400 italic">No worklogs yet</div>
                  ) : (
                    worklogs.map((wl) => {
                      const isEditing = editingWorklogId === wl.id;
                      const commentText = extractWorklogComment(wl.comment);
                      const authorName = wl.author?.displayName || 'Unknown';
                      const isMine = !!wl.isMine;
                      return (
                        <div key={wl.id} className="py-1 border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                          {isEditing ? (
                            <div className="flex flex-wrap gap-1.5 items-center">
                              <input
                                type="text"
                                value={editWorklogComment}
                                onChange={(e) => setEditWorklogComment(e.target.value)}
                                className="flex-1 min-w-[6rem] px-1 py-0.5 text-xs border-b border-slate-200 dark:border-slate-600 bg-transparent dark:text-white focus:outline-none"
                                placeholder="Comment"
                              />
                              <input
                                type="text"
                                value={editTimeSpent}
                                onChange={(e) => setEditTimeSpent(e.target.value)}
                                className="w-12 px-1 py-0.5 text-xs border-b border-slate-200 dark:border-slate-600 bg-transparent dark:text-white focus:outline-none"
                                placeholder="h"
                              />
                              <button
                                type="button"
                                disabled={loggingWork}
                                onClick={() => handleUpdateWorklog(wl.id)}
                                className="text-[10px] text-blue-600 hover:underline"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingWorklogId(null)}
                                className="text-[10px] text-gray-500 hover:underline"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 text-xs">
                                <span className={isMine ? 'text-blue-600 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-200'}>
                                  {authorName}
                                </span>
                                <span className="ml-1.5 font-semibold text-gray-800 dark:text-gray-100">
                                  {formatSecondsToHours(wl.timeSpentSeconds)}h
                                </span>
                                {commentText && (
                                  <span className="ml-1.5 text-gray-500 dark:text-gray-400 truncate">
                                    · {commentText}
                                  </span>
                                )}
                              </div>
                              {isMine && (
                                <div className="flex gap-1.5 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => startEditWorklog(wl)}
                                    className="text-[10px] text-blue-600 hover:underline"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    disabled={loggingWork}
                                    onClick={() => handleDeleteWorklog(wl.id)}
                                    className="text-[10px] text-red-500 hover:underline"
                                  >
                                    Del
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailPanel;
